/**
 * Página de Servicios y Procedimientos - Portal de Transparencia
 * Estilo Awwwards Interior - Sistema Interno de Información
 * 
 * Esta página presenta información sobre el cumplimiento normativo
 * relacionado con el Sistema Interno de Información o Canal de Denuncias
 * según la Ley 2/2023.
 */

import React, { useRef, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de Servicios y Procedimientos con estilo Awwwards
 * Explica el cumplimiento de INTERCASA respecto a la Ley 2/2023
 * @returns {JSX.Element} Página de servicios y procedimientos
 */
const ServiciosProcedimientosPage = () => {
  const heroRef = useRef(null);
  const location = useLocation();

  /**
   * Efecto para hacer scroll al inicio de la página al cargar
   */
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.key]);

  /**
   * Efecto para animar los elementos del hero al cargar la página
   * Anima título y subtítulo con efecto fadeUp
   */
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    
    // Animación del título
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );
    
    // Animación del subtítulo
    tl.fromTo(hero.querySelector('.hero-subtitle'),
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
      '-=0.4'
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO SECTION - Estilo Portal Transparencia
        =============================================
      */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        {/* Contenido del hero */}
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <Link 
              to="/transparencia"
              className="group inline-flex items-center text-white/60 hover:text-white mb-8 transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium tracking-wide">Portal de Transparencia</span>
            </Link>
            
            <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
              Cumplimiento Normativo
            </span>
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] mb-6 drop-shadow-lg">
              Servicios y<br />
              <span className="text-intercasa-yellow">Procedimientos</span>
            </h1>
            
            <p className="hero-subtitle text-lg md:text-xl text-white/60 max-w-2xl font-light">
              Sistema Interno de Información según Ley 2/2023
            </p>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        2. CONTENIDO PRINCIPAL
        =============================================
      */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Contenido */}
            <AnimatedSection animation="fadeUp" className="mb-16">
              <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                <p className="text-intercasa-dark/80 leading-relaxed mb-6">
                  De conformidad con lo establecido en el artículo 10.1 de la Ley 2/2023, de 20 de febrero, reguladora de la protección de las personas que informen sobre infracciones normativas y de lucha contra la corrupción, la obligación de disponer de un Sistema Interno de Información o Canal de Denuncias recae únicamente sobre las entidades que cuenten con cincuenta o más personas trabajadoras.
                </p>
                <p className="text-intercasa-dark font-medium">
                  En este sentido, INTERCASA, S.A. no se encuentra actualmente obligada a la implantación de dicho sistema, al contar con una plantilla inferior a 50 empleados.
                </p>
              </div>

              {/* Botones de descarga */}
              <div className="flex flex-wrap gap-4 justify-center mt-8">
                <a 
                  href="/PortalTansparenciaIntercasa/ServicioyProcedimiento/ServiciosyProcedimientos.pdf" 
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Descargar PDF
                </a>
                <a
                  href="/PortalTansparenciaIntercasa/ServicioyProcedimiento/ServiciosyProcedimientos.odt"
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Descargar ODT
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default ServiciosProcedimientosPage;
